import React, { useEffect, useRef, useState } from 'react'
import { AdyenCheckout, PayPal } from '@adyen/adyen-web'
import { useCurrentBasket } from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import { useCurrentCustomer } from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import { useAccessToken, usePaymentMethodsForBasket, useShippingMethodsForShipment, useShopperBasketsMutation, useShopperOrdersMutation, ShopperOrdersMutations } from '@salesforce/commerce-sdk-react'
import { useIntl } from 'react-intl'
import { getSiteEnvVarValue, getBrandSiteEnvVarValue } from '../../../utils/site-config'
import { resolveBrandFromUrl } from '../../../../../contexts/brand-context'
import { sendClickEvent, sendBeginCheckoutEvent } from '../../../utils/gtm-utils'
import usedDeviceDetection from '../../../hooks/use-device-detection'
//fetch SFCC payment methods and filter out & return the PayPal payment method
const usePayPalPaymentMethod = (basket) => {
    const { data: paymentMethods } = usePaymentMethodsForBasket({
        parameters: { basketId: basket?.basketId }
    })
    const adyenPaymentInstrument = paymentMethods?.applicablePaymentMethods?.find(
        ({ id }) => id === 'AdyenComponent'
    )
    const adyenPaymentMethods = adyenPaymentInstrument?.c_adyenPaymentMethods
    return adyenPaymentMethods?.paymentMethods?.find(({ type }) => type === 'paypal')
}

const getEstimatedBasketTotal = (basket) => {
    const discountedMerchandiseTotal = Number(basket?.productTotal ?? basket?.productSubTotal) || 0
    const estimatedShipping = Number(basket?.shippingTotal) || 0
    const estimatedTax = basket?.taxTotal != null ? Number(basket.taxTotal) : 0
    const estimatedTotalValue =
        basket?.orderTotal > 0
            ? Number(basket.orderTotal)
            : (Number(basket?.productTotal) || discountedMerchandiseTotal) + estimatedShipping + estimatedTax
    return estimatedTotalValue
}

// if shipping method is applied to basket return basket shipping amount else return shipping method price
const getApplicableShippingMethodPrice = (shippingMethod, basket) => {
    return Math.round(
        Number(basket.shipments[0].shippingMethod?.id == shippingMethod.id ? basket?.shippingTotal : (shippingMethod.price || 0)) * 100
    )
}

const PayPalExpressButton = ({ setErrorMessage }) => {
    // create a PayPal container div and then mount the Adyen PayPal Express button to it
    const paypalContainerRef = useRef(null)
    const paypalComponentRef = useRef(null)
    const orderNoRef = useRef(null)
    const adyenPaymentProcessIDRef = useRef(null)
    const createOrderResponseRef = useRef(null) // to save the new order created in SFCC after Paypal authorization in onSubmit
    const pspReferenceRef = useRef(null) // pspReference will be used in every updateOrder call
    const paypalPaymentDataRef = useRef(null)
    const isShippingAddressValidRef = useRef(null)

    let { data: basket, refetch: refetchCurrentBasket } = useCurrentBasket()
    // fetch the Paypal payment method for the basket from applicable payment methods in SFCC
    const paypalPaymentMethod = usePayPalPaymentMethod(basket)

    // fetch the shipping methods for the shipment in the basket from SFCC
    const shipmentId = basket?.shipments?.[0]?.shipmentId
    const { data: shippingMethods } = useShippingMethodsForShipment({
        parameters: {
            basketId: basket?.basketId,
            shipmentId
        }
    })
    const paypalExpressShippingMethods = shippingMethods?.applicableShippingMethods?.filter((shippingMethod) => {
        return shippingMethod?.c_isAvailableForPaypalExpress
    })

    const { data: customer } = useCurrentCustomer()
    const { isRegistered } = customer || {}

    const { mutateAsync: updateShippingMethodForShipment } = useShopperBasketsMutation('updateShippingMethodForShipment')
    const { mutateAsync: updateShipmentForBasket } = useShopperBasketsMutation('updateShipmentForBasket')
    const { mutateAsync: updateShippingAddressForShipment } = useShopperBasketsMutation('updateShippingAddressForShipment')
    const { mutateAsync: updateBillingAddressForBasket } = useShopperBasketsMutation('updateBillingAddressForBasket')
    const { mutateAsync: addPaymentInstrumentToBasket } = useShopperBasketsMutation('addPaymentInstrumentToBasket')
    const { mutateAsync: updateCustomerForBasket } = useShopperBasketsMutation('updateCustomerForBasket')
    const { mutateAsync: removePaymentInstrumentFromBasket } = useShopperBasketsMutation('removePaymentInstrumentFromBasket');
    const { mutateAsync: updateBasket } = useShopperBasketsMutation('updateBasket')
    const { mutateAsync: createOrder } = useShopperOrdersMutation('createOrder')

    const { app: appConfig } = getConfig()
    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const proxyPath = appConfig.commerceAPI.proxyPath
    const organizationId = parameters.organizationId

    const { locale, site, buildUrl } = useMultiSite()
    const siteId = site?.id
    const brandId = resolveBrandFromUrl(
        typeof window === 'undefined' ? '' : window.location.hostname
    )
    const deviceType = usedDeviceDetection()
    const intl = useIntl()
    const { getTokenWhenReady } = useAccessToken()
    const ADYEN_CLIENT_KEY = getSiteEnvVarValue(appConfig, 'ADYEN_CLIENT_KEY', siteId)
    const ADYEN_MODE = getSiteEnvVarValue(appConfig, 'ADYEN_MODE', siteId)


    // update the SFCC order's Payment Instrument with the result recieved from Adyen authorization in onSubmit
    const updatePaymentInstrument = useShopperOrdersMutation(ShopperOrdersMutations.UpdatePaymentInstrumentForOrder)
    const updateOrderPaymentInstrument = async (adyenPaymentResult) => {
        const createOrderResponse = createOrderResponseRef.current

        const adyenInstrument = createOrderResponse.paymentInstruments.find(
            (paymentInstrument) => paymentInstrument.paymentMethodId === 'AdyenComponent')

        const updatedOrderWithPaymentIntrument = await updatePaymentInstrument.mutateAsync({
            parameters: {
                orderNo: createOrderResponse.orderNo,
                paymentInstrumentId: adyenInstrument.paymentInstrumentId
            },
            body: {
                amount: adyenPaymentResult?.amount?.value
                    ? adyenPaymentResult.amount.value / 100
                    : undefined,
                paymentMethodId: 'AdyenComponent',
                c_adyenPaymentData: JSON.stringify(adyenPaymentResult)
            }
        })

    }

    const callPaypalUpdateAPI = async (actions, component = {}, deliveryMethods = null, deliveryAddress = null) => {
        const paypalUpdateOrderBody = {
            pspReference: pspReferenceRef.current,
            paymentData: paypalPaymentDataRef.current,
            amount: {
                currency: basket.currency,
                value: Math.round(getEstimatedBasketTotal(basket) * 100)
            },
            shippingAmount: {
                currency: basket.currency,
                value: Math.round(Number(basket?.shippingTotal) * 100)
            },
            taxTotal: {
                amount: {
                    currency: basket.currency,
                    value: Math.round(Number(basket?.taxation != 'gross' ? basket?.taxTotal : 0) * 100)
                }
            }
        }
        if (deliveryMethods) {
            paypalUpdateOrderBody.deliveryMethods = deliveryMethods
        }
        if (deliveryAddress) {
            paypalUpdateOrderBody.deliveryAddress = deliveryAddress
        }

        const token = await getTokenWhenReady()
        const paypalUpdateResponse = await fetch(
            `${proxyPath}/custom/adyen-paypal-updateorder/v1/organizations/${organizationId}/adyen-paypal-updateorder?siteId=${siteId}&c_brand=Garage`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(paypalUpdateOrderBody)
            }
        )

        const paypalUpdateResult = await paypalUpdateResponse.json()

        if (paypalUpdateResult.errorCode || paypalUpdateResult.status === 'error') {
            actions.reject(paypalUpdateResult)
            return null
        }
        paypalPaymentDataRef.current = paypalUpdateResult.paymentData
        component?.updatePaymentData?.(paypalUpdateResult.paymentData)
        return paypalUpdateResult
    }

    // set fingerPrint required for Adyen authorization of applepay onSubmit
    const [fingerprintScriptLoaded, setFingerprintScriptLoaded] = useState(false)
    useEffect(() => {
        if (!document.getElementById('adyen-device-fingerprint')) {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.id = 'adyen-device-fingerprint'
            input.name = 'adyen-device-fingerprint'
            document.body.appendChild(input)
        }

        if (!fingerprintScriptLoaded) {
            const script = document.createElement('script')

            const now = new Date()
            const yyyy = now.getFullYear()
            const mm = String(now.getMonth() + 1).padStart(2, '0')
            const dd = String(now.getDate()).padStart(2, '0')
            const version = `${yyyy}${mm}${dd}`

            script.src = `https://live.adyen.com/hpp/js/df.js?v=${version}`
            script.async = true
            script.nonce = window.__CSP_NONCE__

            script.onload = () => {
                setFingerprintScriptLoaded(true)
                window.dfSet('adyen-device-fingerprint')
            }

            document.body.appendChild(script)
        }
    }, [fingerprintScriptLoaded])

    useEffect(() => {
        let isPaypalMounted = true

        const initAdyenCheckoutAndLoadPaypalInContainer = async () => {
            // if paypal was already opened once clear the previous paypal component and reinitialize it
            if (paypalComponentRef.current) {
                paypalComponentRef.current.unmount()
                paypalComponentRef.current = null
            }

            const countryCode = locale.id.split('-')[1]
            const adyenAmountObj = {
                value: Math.round(getEstimatedBasketTotal(basket) * 100),
                currency: basket.currency
            }


            const adyenCheckoutObj = await AdyenCheckout({
                clientKey: ADYEN_CLIENT_KEY,
                environment: ADYEN_MODE,
                locale: locale.id,
                countryCode,
                amount: adyenAmountObj,
                paymentMethodsResponse: {
                    paymentMethods: [paypalPaymentMethod]
                },
                onSubmit: async (state, component, actions) => {
                    //send gtm event
                    sendClickEvent('paypal_cta_cartpage')
                    sendBeginCheckoutEvent(basket, {
                        checkout_entry_method: 'paypal_express',
                        checkout_location: 'cart_page',
                        checkout_type: 'express',
                        payment_type: 'paypal'
                    })
                    // called whn user clicks on Paypal Express button and a new empty Paypal modal opens
                    // clear any previous error message before opening the Paypal modal
                    setErrorMessage('')
                    orderNoRef.current = null; // reset any order number previously created
                    adyenPaymentProcessIDRef.current = null; // reset any adyenPaymentProcessID previously created

                    // call Paypal /payments API via /adyen-payments SCAPI
                    const token = await getTokenWhenReady()
                    const adyemnPaymentBody = {
                        orderId: basket.basketId,
                        orderToken: 'express-checkout',
                        adyenData: state,
                        amount: {
                            currency: basket.currency,
                            value: Math.round(Number(basket?.productTotal ?? basket?.productSubTotal) * 100)
                        },
                        deviceFingerprint: document.getElementById('adyen-device-fingerprint').value,
                        locale,
                        returnUrl: new URL(buildUrl('/cart'), window.location.origin).toString()
                    }
                    const paymentResult = await fetch(
                        `${proxyPath}/custom/adyen-payments/v1/organizations/${organizationId}/adyen-payments?siteId=${siteId}&c_brand=Garage`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify(adyemnPaymentBody)
                        }
                    )

                    const result = await paymentResult.json()


                    if (!result.resultCode || result.resultCode === 'Error') {
                        actions.reject()
                        return
                    }
                    pspReferenceRef.current = result.pspReference
                    paypalPaymentDataRef.current = result.action.paymentData
                    orderNoRef.current = result.adyenReferenceNo
                    adyenPaymentProcessIDRef.current = result.adyenPaymentProcessID
                    //update paypal with shipping methods
                    // first shipping method from paypal express shipping methods is selected by default on paypal modal
                    //update basket with default shipping method
                    // const selectedShippingMethod;
                    basket = await updateShippingMethodForShipment({
                        parameters: {
                            basketId: basket.basketId,
                            shipmentId: basket.shipments[0].shipmentId
                        },
                        body: {
                            id: basket.shipments[0].shippingMethod.id || shippingMethods?.defaultShippingMethodId
                        }
                    })
                    const shippingMethodsToSendToPaypal = paypalExpressShippingMethods?.map(
                        (shippingMethod, index) => ({
                            reference: shippingMethod.id,
                            description: shippingMethod.name,
                            amount: {
                                currency: basket.currency,
                                value: getApplicableShippingMethodPrice(shippingMethod, basket)
                            },
                            selected: basket.shipments[0].shippingMethod ? shippingMethod.id == basket.shipments[0].shippingMethod.id : shippingMethod.id == shippingMethods?.defaultShippingMethodId
                        })
                    ) || []
                    await callPaypalUpdateAPI(actions, component, shippingMethodsToSendToPaypal)
                    // remove any existing payment instruments from the basket before adding the new Paypal payment instrument to the basket
                    for (const paymentInstrument of basket.paymentInstruments || []) {
                        if(paymentInstrument.paymentMethodId !== 'LOYALTY_POINTS') {
                            await removePaymentInstrumentFromBasket({
                                parameters: {
                                    basketId: basket.basketId,
                                    paymentInstrumentId: paymentInstrument.paymentInstrumentId
                                }
                            });
                        }
                    }
                    const signifydParameters = {
                        paymentMethod: state.data.paymentMethod
                    }

                    const signifydJsonString = JSON.stringify(signifydParameters)

                    await addPaymentInstrumentToBasket({
                        parameters: {
                            basketId: basket.basketId
                        },
                        body: {
                            paymentMethodId: 'AdyenComponent',
                            amount: getEstimatedBasketTotal(basket),
                            c_signifydPaymentData: signifydJsonString
                        }
                    })
                    actions.resolve(result) // this will load paypal HTML on modal
                },

                onAdditionalDetails: async (state, component, actions) => {
                    // called after onAuthorized
                    // create order from basket in SFCC
                    const token = await getTokenWhenReady()
                    const order = await createOrder({
                        parameters: { c_generatedOrderNo: orderNoRef.current, c_deviceType: deviceType },
                        body: { basketId: basket.basketId }
                    })
                    if (order?.status === "failed") {
                        actions.reject() // calls onPaymentFailed
                        return
                    }
                    createOrderResponseRef.current = order


                    try {
                        const adyenPaymentDetailsBody = {
                            ...state.data,
                            orderNo: orderNoRef.current,
                            adyenPaymentProcessID: adyenPaymentProcessIDRef.current,
                            paymentData: paypalPaymentDataRef.current
                        }
                        const adyenPaymentDetailsResponse = await fetch(
                            `${proxyPath}/custom/adyen-payments-details/v1/organizations/${organizationId}/adyen-payments-details?siteId=${siteId}`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${token}`
                                },
                                body: JSON.stringify(adyenPaymentDetailsBody)
                            }
                        )
                        const adyenPaymentDetailResult = await adyenPaymentDetailsResponse.json()

                        // update order with payment instrument with the result from Adyen /payments/details API call
                        // this will place or fail the order based on the resultCode from Adyen
                        await updateOrderPaymentInstrument(adyenPaymentDetailResult.adyenData)

                        // If the /payments/details request from your server fails, or if an unexpected error occurs.
                        if (!adyenPaymentDetailResult.adyenData && !adyenPaymentDetailResult?.adyenData?.resultCode) {
                            actions.reject(adyenPaymentDetailResult) // calls onPaymentFailed
                            return
                        }

                        const { resultCode, action, order, donationToken } = adyenPaymentDetailResult.adyenData

                        // calls onPaymentCompleted
                        actions.resolve({
                            resultCode,
                            action,
                            order,
                            donationToken
                        })
                    } catch (error) {
                        await updateOrderPaymentInstrument()
                        actions.reject()
                    }
                },
                onPaymentCompleted: async (result, component) => {

                    const createOrderResponse = createOrderResponseRef.current


                    if (result?.resultCode?.toLowerCase() === 'authorised') {
                        window.location.href = buildUrl(`/checkout/confirmation/${createOrderResponse?.orderNo || ''}`)
                    }
                },

                onPaymentFailed: (result, component) => {
                    setErrorMessage(result?.message || intl.formatMessage({
                        id: 'checkout.message.generic_banner_error',
                        defaultMessage:
                            'There’s a problem with your payment. Please try again or try a different payment method. If the problem persists, contact our customer service at 0800 206 2797.'
                    }))

                    component?.setStatus?.('ready')
                    paypalComponentRef.current?.unmount()
                    paypalComponentRef.current = null
                    initAdyenCheckoutAndLoadPaypalInContainer()
                },
                onError: (error, component) => {
                    error != 'CANCEL' ? setErrorMessage(error?.message || intl.formatMessage({
                        id: 'checkout.message.generic_banner_error',
                        defaultMessage:
                            'There’s a problem with your payment. Please try again or try a different payment method. If the problem persists, contact our customer service at 0800 206 2797.'
                    })) : ''

                    component?.setStatus?.('ready')
                    paypalComponentRef.current?.unmount()
                    paypalComponentRef.current = null
                    initAdyenCheckoutAndLoadPaypalInContainer()
                }
            })


            //create paypal component/HTML and mount it to the container
            paypalComponentRef.current = new PayPal(adyenCheckoutObj, {
                isExpress: true,
                expressPage: 'cart',
                blockPayPalCreditButton: true,
                blockPayPalPayLaterButton: true,
                blockPayPalVenmoButton: true,
                environment: ADYEN_MODE,
                amount: adyenAmountObj,
                countryCode: locale.id.split('-')[1],
                merchantId: 'MQJ5N6VMFTMGY',
                showBrandIcon: true,
                style: {
                    layout: 'vertical',
                    label: 'paypal',
                    color: 'white',
                    shape: 'sharp',
                    height: 48
                },
                onAuthorized: async (data, actions) => {
                    const { authorizedEvent, billingAddress, deliveryAddress } = data
                    if (countryCode !== deliveryAddress.country) {
                        actions.reject() // will call onError and close the Paypal modal
                        return
                    }
                    const customerEmail = isRegistered ? customer?.email : data?.authorizedEvent?.payer?.email_address
                    if (customerEmail) {
                        await updateCustomerForBasket({
                            parameters: { basketId: basket.basketId },
                            body: { email: customerEmail }
                        })
                    }
                    await updateShippingAddressForShipment({
                        parameters: {
                            basketId: basket.basketId,
                            shipmentId: basket.shipments[0].shipmentId
                        },
                        body: {
                            firstName: data?.authorizedEvent?.payer?.name?.given_name,
                            lastName: data?.authorizedEvent?.payer?.name?.surname,
                            address1: deliveryAddress.street,
                            city: deliveryAddress.city,
                            stateCode: deliveryAddress.stateOrProvince,
                            postalCode: deliveryAddress.postalCode,
                            countryCode: deliveryAddress.country
                        }
                    })
                    const shippingAddressToSendToPaypal = {
                        firstName: data?.authorizedEvent?.payer?.name?.given_name,
                        lastName: data?.authorizedEvent?.payer?.name?.surname,
                        houseNumberOrName: deliveryAddress?.houseNumberOrName,
                        street: deliveryAddress?.street,
                        city: deliveryAddress?.city,
                        stateOrProvince: deliveryAddress?.stateOrProvince,
                        postalCode: deliveryAddress?.postalCode,
                        country: deliveryAddress?.country
                    }
                    await callPaypalUpdateAPI(actions, null, null, shippingAddressToSendToPaypal)
                    await updateBillingAddressForBasket({
                        parameters: {
                            basketId: basket.basketId
                        },
                        body: {
                            firstName: billingAddress.firstName || deliveryAddress.firstName.trim().split(/\s+/)[0],
                            lastName: billingAddress.lastName || deliveryAddress.firstName.trim().split(/\s+/)[1] || '',
                            address1: billingAddress.street,
                            city: billingAddress.city,
                            stateCode: billingAddress.stateOrProvince,
                            postalCode: billingAddress.postalCode,
                            countryCode: billingAddress.country
                        }
                    })

                    // update basket with custom attributes and brand
                    await updateBasket({
                        parameters: { basketId: basket.basketId },
                        body: {
                            c_brandId: (brandId || basket?.c_brandId || 'garage').toLowerCase(),
                            c_isPayPalExpressOrder: true
                        }
                    })
 
                    actions.resolve() // will call onAdditionalDetails
                },
                onShippingAddressChange: async function (data, actions, component) {
                    const postalCodePrefixesToRestrict = getSiteEnvVarValue(appConfig, 'POSTAL_CODE_PREFIXES_TO_RESTRICT', siteId) //reject address if first 2 characters of postal code are in it
                    if (data.shippingAddress.countryCode != countryCode ||
                        postalCodePrefixesToRestrict?.split(',').includes(data.shippingAddress.postalCode.trim().toUpperCase().substring(0, 2))) {
                        isShippingAddressValidRef.current = false
                        return actions.reject(data.shippingAddress.countryCode != countryCode ? data?.errors?.COUNTRY_ERROR : data?.errors?.ADDRESS_ERROR);
                    }
                    isShippingAddressValidRef.current = true
                    basket = await updateShippingAddressForShipment({
                        parameters: {
                            basketId: basket.basketId,
                            shipmentId: basket.shipments[0].shipmentId
                        },
                        body: {
                            firstName: data?.shippingAddress?.firstName || 'test',
                            lastName: data?.shippingAddress?.lastName || 'test',
                            address1: data?.shippingAddress?.street || '',
                            city: data?.shippingAddress?.city || '',
                            stateCode: data?.shippingAddress?.state || '',
                            postalCode: data?.shippingAddress?.postalCode || '',
                            countryCode: data?.shippingAddress?.countryCode || ''
                        }
                    })

                    await callPaypalUpdateAPI(actions, component)
                },
                onShippingOptionsChange: async (data, actions, component) => {
                    // called when user selects a shipping option in the Paypal modal
                    if (!isShippingAddressValidRef.current) {
                        actions.reject()
                        component?.setStatus?.('ready')
                        return
                    }
                    basket = await updateShippingMethodForShipment({
                        parameters: {
                            basketId: basket.basketId,
                            shipmentId: basket.shipments[0].shipmentId
                        },
                        body: {
                            id: data.selectedShippingOption.id
                        }
                    })
                    const shippingMethodsToSendToPaypal = paypalExpressShippingMethods?.map(
                        (shippingMethod, index) => ({
                            reference: shippingMethod.id,
                            description: shippingMethod.name,
                            amount: {
                                currency: basket.currency,
                                value: getApplicableShippingMethodPrice(shippingMethod, basket)
                            },
                            selected: shippingMethod.id == data.selectedShippingOption.id
                        })
                    ) || []
                    await callPaypalUpdateAPI(actions, component, shippingMethodsToSendToPaypal)
                },
                cspNonce: window.__CSP_NONCE__
            })
            await paypalComponentRef.current.isAvailable()
            if (!isPaypalMounted) {
                paypalComponentRef.current?.unmount()
                paypalComponentRef.current = null
                return
            }
            paypalComponentRef.current.mount(paypalContainerRef.current)
        }

        if (window.paypal && !window.paypal.FUNDING) {
            window.paypal = undefined
        }

        if (basket && paypalPaymentMethod) {
            paypalComponentRef.current?.unmount()
            paypalComponentRef.current = null
            initAdyenCheckoutAndLoadPaypalInContainer()
        }

        return () => {
            isPaypalMounted = false

            if (paypalComponentRef.current) {
                paypalComponentRef.current.unmount()
                paypalComponentRef.current = null
            }
        }

    }, [paypalPaymentMethod])

    return (
        <div ref={paypalContainerRef} />
    )
}

export default PayPalExpressButton